<template>
  <div class="input-range-slider" :class="classModifiers">
    <div class="input-range-slider__track-container">
      <div class="input-range-slider__track-background" />
      <div class="input-range-slider__track-fill" :style="fillWidth" />
      <div v-if="props.showTicks" class="input-range-slider__ticks">
        <div v-for="tick in tickCount" :key="tick" class="input-range-slider__tick"></div>
      </div>
      <input
        :key="sliderKey"
        v-model="rangeInputModel"
        class="input-range-slider__input"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :disabled="isDisabled"
      />
    </div>
    <div class="range-value">
      <span class="range-value__value-unit">{{ model }} {{ unit }}</span>
      <template v-if="autoModeEnabled">
        <sp-button class="range-value__button" :variant="toggleButtonVariant" size="x-small" @click="toggleAutoMode">
          Auto
        </sp-button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { AUTO_VALUE } from "#ce/utils/css";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: () => undefined,
  },
  auto: booleanAttribute(),
  min: {
    type: [Number, String],
    required: true,
  },
  max: {
    type: [Number, String],
    required: true,
  },
  defaultValue: {
    type: [Number, String],
    default: undefined,
  },
  step: {
    type: [Number, String],
    required: true,
  },
  unit: {
    type: String,
    default: undefined,
  },
  disabled: booleanAttribute(),
  showTicks: booleanAttribute(),
  ticksInset: booleanAttribute(true),
  valueAndUnitWidth: {
    type: String,
    default: "8ch",
    validator: (value) => {
      return /^-?\d*\.?\d+(ch|rem)$/.test(value);
    },
  },
  valueButtonWidth: {
    type: String,
    default: "5ch",
    validator: (value) => {
      return /^-?\d*\.?\d+(ch|rem)$/.test(value);
    },
  },
});

const min = computed(() => +props.min);
const max = computed(() => +props.max);
const step = computed(() => +props.step);
const defaultValue = computed(() => props.defaultValue ?? props.min);

const autoModeEnabled = computed(() => toBoolean(props.auto));
const isDisabled = computed(() => toBoolean(props.disabled));
const model = computed({
  get() {
    return props.modelValue ?? defaultValue.value;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const isAuto = computed(() => autoModeEnabled.value && model.value === AUTO_VALUE);
/**
 * A dedicated model for the slider.
 * This is a necessary workaround to handle the auto mode.
 * If auto mode is enabled, we need to set the model to the default value in order to force the slider moving to the start.
 * Since this is a computed property, it will be updated when the model is updated.
 * This is why we need to check if the value is the default value and if so, we don't update the model.
 */
const rangeInputModel = computed({
  /**
   * If the auto mode is enabled, we need to return the default value, which will force the slider to move to the start.
   * Otherwise, we return the model value as is.
   */
  get() {
    return isAuto.value ? defaultValue.value : model.value;
  },
  /**
   * If the auto mode is enabled, we need to check if the value is the default value. (The value we just manually set in the get method)
   * If so, we don't update the model.
   * Otherwise, we update the model as usual.
   */
  set(value) {
    // Ignore the min value if auto mode is enabled.
    if (isAuto.value && value === defaultValue.value) {
      return;
    }
    model.value = value;
  },
});

const sliderKey = computed(() => `${props.min}-${props.max}-${props.step}`);
const toggleButtonVariant = computed(() => (isAuto.value ? "flat" : "outlined"));

const classModifiers = computed(() => ({
  "--auto-mode": props.auto,
  "--is-auto": isAuto.value,
  "--ticks-inset": props.ticksInset,
  "--show-tick-labels": props.showTickLabels,
  "--show-ticks": props.showTicks,
}));

function toggleAutoMode() {
  model.value = isAuto.value ? defaultValue.value : AUTO_VALUE;
}

const fillWidth = computed(() => {
  const range = props.max - props.min;

  if (range === 0) {
    return 0;
  }

  const value = model.value - props.min;

  // Clamp the percentage value between 0% and 100%,
  // in case `value` is below 0 or exceeds the defined `range`.
  const computedWidth = Math.max(0, Math.min((value / range) * 100, 100));
  // Return the width as a CSS style string
  return `width: ${computedWidth}%;`;
});

const tickCount = computed(() => Math.floor((props.max - props.min) / props.step) + 1);
</script>

<style>
:host {
  --track-size: var(--sp-input-range-slider-track-size, 6px);
  --track-color: var(--sp-input-range-slider-track-color, #ccc);
  --track-border-radius: var(--sp-input-range-slider-track-border-radius, calc(var(--track-size) / 2));
  --track-fill-color: var(--sp-input-range-slider-track-fill-color, var(--sp-sys-color-primary, #1976d2));
  --track-hover-cursor: var(--sp-input-range-slider-track-hover-cursor, pointer);
  --thumb-size: var(--sp-input-range-slider-thumb-size, 16px);
  --thumb-color: var(--sp-input-range-slider-thumb-color, var(--sp-sys-color-primary, #1976d2));
  --thumb-border-color: var(--sp-input-range-slider-thumb-border-color, transparent);
  --thumb-border-width: var(--sp-input-range-slider-thumb-border-width, 0px);
  --thumb-border-radius: var(--sp-input-range-slider-thumb-border-radius, 50%);

  display: block;
}
</style>

<style lang="scss" scoped>
.input-range-slider {
  $block: &;
  --value-and-unit-width: v-bind(valueAndUnitWidth);
  --value-button-width: v-bind(valueButtonWidth);
  --range-value-width: auto;
  --range-value-opacity: 1;
  display: grid;
  grid-template-columns: 1fr var(--range-value-width);
  align-items: center;

  sp-button {
    --sp-comp-button-font-short: var(--sp-sys-form-field-label-short);
    opacity: 0.66;
  }

  .range-value {
    display: flex;
    align-items: center;
    justify-content: end;
  }

  .range-value__value-unit {
    text-align: right;
    opacity: var(--range-value-opacity);
    width: var(--value-and-unit-width);
  }

  &.--auto-mode {
    .range-value {
      display: grid;
      grid-template-columns: var(--value-and-unit-width) var(--value-button-width);
      grid-gap: 0.75ch;
    }

    &.--is-auto {
      --range-value-opacity: 0;

      sp-button {
        opacity: 1;
      }
    }
  }

  &__track-container {
    position: relative;
    width: 100%;
    height: var(--track-size);
  }

  &__track-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--track-color);
    border-radius: var(--track-border-radius);
    z-index: 0;
  }

  &__track-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: var(--track-fill-color);
    border-radius: var(--track-border-radius) 0 0 var(--track-border-radius);
    z-index: 1;

    // Make sure the fill width is dynamically calculated from the thumb’s center to avoid layout gaps or visual overlaps
    min-width: calc(var(--thumb-size) / 2);
    max-width: calc(100% - var(--thumb-size) / 2);
  }

  &__input {
    display: block;
    appearance: none;
    width: 100%;
    height: var(--track-size);
    background-color: transparent;
    outline: none;
    position: absolute;
    top: 0;
    z-index: 3;
    padding: 0;
    margin: 0;

    &:hover {
      cursor: var(--track-hover-cursor);
    }

    &::-webkit-slider-runnable-track {
      background: transparent;
      height: var(--track-size);
    }

    &::-webkit-slider-thumb {
      appearance: none;
      width: var(--thumb-size);
      height: var(--thumb-size);
      background-color: var(--thumb-color);
      border-radius: 50%;
      cursor: pointer;
      border: none;
      margin-top: calc((var(--thumb-size) - var(--track-size)) / -2);
    }

    &::-moz-range-track {
      background: transparent;
      height: var(--track-size);
    }

    &::-moz-range-thumb {
      width: var(--thumb-size);
      height: var(--thumb-size);
      background-color: var(--thumb-color);
      border-radius: 50%;
      cursor: pointer;
      border: none;
    }
  }

  &__ticks {
    position: absolute;
    top: calc(var(--track-size) + var(--sp-input-range-slider-ticks-height, 2px));
    left: calc(var(--thumb-size) / 2);
    width: calc(100% - var(--thumb-size));
    display: flex;
    justify-content: space-between;
    align-items: center;
    pointer-events: none;
    z-index: 3;
    height: var(--sp-input-range-slider-ticks-height, var(--track-size));
  }

  &__tick {
    position: relative;
    width: 1px;
    background-color: var(--sp-input-range-slider-tick-color, #ccc);
    height: var(--track-size);
  }

  &.--ticks-inset {
    #{$block}__ticks {
      top: 0;
    }
    #{$block}__tick {
      opacity: 0.8;
      border-radius: 50%;
      height: 3px;
      width: 3px;
      background-color: var(--sp-input-range-slider-ticks-inset-tick-color, #fff);

      &:first-child,
      &:last-child {
        opacity: 0;
      }
    }
  }
}
</style>
